import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import { supabase } from '../../../lib/supabase';
import { listInvoiceDocuments, getInvoiceDocumentUrl } from '../../../services/invoiceDocumentsService';
import useEscapeKey from '../../../hooks/useEscapeKey';
import { safeStorageName, displayStorageName } from '../../../utils/safeStorageName';

const DocumentsModal = ({ isOpen, onClose, entityId, entityType, subtitle, onFilesChanged }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const [deleteConfirmFile, setDeleteConfirmFile] = useState(null);
  const fileInputRef = useRef(null);
  // Numero de la derniere lecture lancee : une reponse plus ancienne est ignoree.
  const requestRef = useRef(0);

  const storagePath = `${entityType}/${entityId}`;

  const getFileTypeIcon = (fileName) => {
    const ext = fileName?.split('.')?.pop()?.toLowerCase();
    if (ext === 'pdf') {
      return <Icon name="FileText" size={15} className="text-red-500 flex-shrink-0" />;
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']?.includes(ext)) {
      return <Icon name="Image" size={15} className="text-purple-500 flex-shrink-0" />;
    }
    if (['xls', 'xlsx', 'csv']?.includes(ext)) {
      return <Icon name="FileSpreadsheet" size={15} className="text-green-600 flex-shrink-0" />;
    }
    if (['doc', 'docx']?.includes(ext)) {
      return <Icon name="FileText" size={15} className="text-blue-500 flex-shrink-0" />;
    }
    return <Icon name="File" size={15} className="text-gray-400 flex-shrink-0" />;
  };

  // Renvoie la liste lue, ou null si une lecture plus recente l'a remplacee.
  const fetchFiles = useCallback(async () => {
    if (!entityId) return null;
    const requestId = ++requestRef.current;
    setLoadingFiles(true);
    let list = [];
    try {
      // Source UNIQUE, partagee avec le badge de la ligne : plus deux
      // implementations qui divergent. Elle couvre les trois emplacements,
      // dont la boite de reception qui manquait ici.
      if (entityType === 'sub_invoices') {
        const docs = await listInvoiceDocuments(entityId);
        list = (docs || [])?.map((d) => ({ name: d?.name, _doc: d }));
      } else {
        // Autres entites : listage direct, inchange.
        const { data, error } = await supabase?.storage
          ?.from('contracts-documents')
          ?.list(storagePath, { limit: 100, sortBy: { column: 'name', order: 'asc' } });
        if (error) {
          console.error('Error listing files:', error);
        } else {
          list = (data || [])?.filter(f => f?.name && f?.name !== '.emptyFolderPlaceholder');
        }
      }
    } catch (err) {
      console.error('Unexpected error listing files:', err);
    }
    // Reponse perimee (autre entite ouverte, ou modale fermee entre-temps).
    if (requestId !== requestRef.current) return null;
    setFiles(list);
    setLoadingFiles(false);
    return list;
  }, [storagePath, entityId, entityType]);

  // Chaque ouverture, et chaque changement d'entite, repart d'une liste vide :
  // jamais les fichiers de l'entite precedente a l'ecran. Toute lecture encore
  // en vol devient perimee.
  useEffect(() => {
    requestRef.current += 1;
    setFiles([]);
    setUploadError(null);
    setDeleteConfirmFile(null);
    setLoadingFiles(true);
    if (isOpen && entityId) {
      fetchFiles();
    }
  }, [isOpen, entityType, entityId, fetchFiles]);

  // Echap ferme d'abord la confirmation de suppression, puis la modale.
  useEscapeKey(() => {
    if (deleteConfirmFile) setDeleteConfirmFile(null);
    else onClose?.();
  }, isOpen);

  const uploadFiles = async (fileList) => {
    if (!fileList?.length) return;
    setUploadError(null);
    setUploading(true);

    const MAX_SIZE = 20 * 1024 * 1024;
    const errors = [];

    for (const file of Array.from(fileList)) {
      if (file?.size > MAX_SIZE) {
        errors?.push(`${file?.name}: ${t('documents.errorFileTooLarge')}`);
        continue;
      }
      // Meme regle que les pieces jointes a la creation : nom lisible, prefixe
      // horodate, et jamais d'ecrasement d'un fichier existant.
      const filePath = `${storagePath}/${Date.now()}_${safeStorageName(file?.name)}`;
      try {
        const { error: uploadErr } = await supabase?.storage
          ?.from('contracts-documents')
          ?.upload(filePath, file, { upsert: false, cacheControl: '3600' });
        if (uploadErr) {
          errors?.push(`${file?.name}: ${uploadErr?.message}`);
        }
      } catch (err) {
        errors?.push(`${file?.name}: ${t('documents.errorUnexpected')}`);
      }
    }

    if (errors?.length) {
      setUploadError(errors?.join('\n'));
    }
    setUploading(false);
    // Une seule relecture : le compte vient de la liste qu'elle renvoie.
    const list = await fetchFiles();
    if (onFilesChanged && list) onFilesChanged(list?.length);
  };

  const handleFileInputChange = (e) => {
    uploadFiles(e?.target?.files);
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    uploadFiles(e?.dataTransfer?.files);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragOver(true);
  };

  const handleDragEnter = (e) => {
    e?.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const deleteFile = async (fileName) => {
    setDeletingFile(fileName);
    const filePath = `${storagePath}/${fileName}`;
    try {
      const { error } = await supabase?.storage
        ?.from('contracts-documents')
        ?.remove([filePath]);
      if (error) {
        alert(t('documents.errorCouldNotDelete', { message: error?.message }));
      } else {
        setFiles((prev) => {
          const updated = prev?.filter((f) => f?.name !== fileName);
          if (onFilesChanged) onFilesChanged(updated?.length);
          return updated;
        });
      }
    } catch (err) {
      alert(t('documents.errorDeleting'));
    } finally {
      setDeletingFile(null);
    }
  };

  const openFile = async (fileName, fileMeta) => {
    try {
      // Le document porte son bucket et son chemin : plus de reconstruction
      // a la main, donc plus de divergence possible avec le listage.
      const url = fileMeta?._doc
        ? await getInvoiceDocumentUrl(fileMeta?._doc)
        : await (async () => {
            const { data, error } = await supabase?.storage
              ?.from('contracts-documents')
              ?.createSignedUrl(`${storagePath}/${fileName}`, 60 * 60);
            if (error) throw error;
            return data?.signedUrl || null;
          })();
      if (url) window.open(url, '_blank');
      else alert(t('documents.errorOpening'));
    } catch (err) {
      alert(t('documents.errorCouldNotOpen', { message: err?.message }));
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-sky-50 flex items-center justify-center">
              <Icon name="FolderOpen" size={18} className="text-sky-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 leading-snug truncate">{t('documents.title')}</h2>
              {subtitle && (
                <p className="text-[11px] text-gray-500 truncate">{subtitle}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* File List */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loadingFiles ? (
            <div className="flex flex-col items-center justify-center gap-2 py-8">
              <Icon name="Loader2" size={24} className="animate-spin text-primary" />
              <p className="text-xs text-gray-400">{t('documents.loading')}</p>
            </div>
          ) : files?.length === 0 ? (
            <div className="text-center py-6">
              <Icon name="FileX" size={32} className="mx-auto mb-2 text-gray-300" />
              <p className="text-xs text-gray-400">{t('documents.noDocumentsAttached')}</p>
            </div>
          ) : (
            <ul className="space-y-1">
              {files?.map((file) => (
                <li key={file?.name} className="group relative">
                  <button
                    onClick={() => openFile(file?.name, file)}
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left"
                  >
                    {getFileTypeIcon(file?.name)}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate group-hover:text-primary transition-colors">
                        {file?.name ? displayStorageName(file?.name)?.charAt(0)?.toUpperCase() + displayStorageName(file?.name)?.slice(1) : ''}
                      </p>
                      {file?.metadata?.size && (
                        <p className="text-[10px] text-gray-400">{formatFileSize(file?.metadata?.size)}</p>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={(e) => { e?.stopPropagation(); setDeleteConfirmFile(file?.name); }}
                    disabled={deletingFile === file?.name}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                    title={t('documents.deleteFileAction')}
                  >
                    {deletingFile === file?.name
                      ? <Icon name="Loader2" size={13} className="animate-spin text-red-400" />
                      : <Icon name="Trash2" size={13} />
                    }
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Upload Zone */}
        <div className="px-5 pb-5 flex-shrink-0">
          {uploadError && (
            <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 whitespace-pre-line">
              {uploadError}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileInputChange}
          />
          <div
            onClick={() => !uploading && fileInputRef?.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            className={`border-2 border-dashed rounded-xl px-4 py-6 text-center cursor-pointer transition-all ${
              isDragOver
                ? 'border-primary bg-orange-50 scale-[1.01]'
                : 'border-gray-200 hover:border-primary hover:bg-orange-50/30'
            } ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Icon name="Loader2" size={20} className="animate-spin text-primary" />
                <p className="text-xs text-gray-500">{t('documents.uploading')}</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Icon name="Upload" size={20} className={isDragOver ? 'text-primary' : 'text-gray-400'} />
                <p className="text-xs font-medium text-gray-600">
                  {t('documents.dropFilesPrefix')}{' '}
                  <span className="text-primary">{t('documents.dropFilesAction')}</span>
                </p>
                <p className="text-[10px] text-gray-400">{t('documents.supportedFormats')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {deleteConfirmFile && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteConfirmFile(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">{t('documents.delete.title')}</h3>
            <p className="text-xs text-gray-500 mb-2">{t('documents.delete.message')}</p>
            <p className="text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 truncate mb-5">
              {displayStorageName(deleteConfirmFile)}
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setDeleteConfirmFile(null)}
                className="px-4 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {t('documents.delete.cancel')}
              </button>
              <button
                onClick={async () => {
                  const name = deleteConfirmFile;
                  setDeleteConfirmFile(null);
                  await deleteFile(name);
                }}
                className="px-4 py-2 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
              >
                {t('documents.delete.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsModal;
